/**
 * Door screen — the most-used action.
 * One press of A → door opens → success animation → return to home.
 *
 * Designed to be ultra fast: minimum delays, immediate feedback,
 * vibration on mobile, success/error sounds.
 */
import { useEffect, useRef, useState } from 'react';
import { doorApi, ApiError } from '@/lib/api';
import { useApp, useDoorMachine } from '@/state/store';
import { sfx } from '@/lib/audio';
import { DoorSprite, DoorOpenSprite, CheckSprite, XSprite, LoadingSprite } from '@/components/Sprites';
import { formatRelative } from '@/lib/format';

// Get the raw zustand store with getState for read-only checks outside React
const useAppStore = useApp;

function handle401(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

function ParticleBurst() {
  // 8 partículas que vuelan en 8 direcciones al abrir la puerta
  const particles = Array.from({ length: 8 }, (_, key) => {
    const angle = (key / 8) * Math.PI * 2;
    const dist = 36 + (key % 3) * 8;
    const bx = Math.cos(angle) * dist;
    const by = Math.sin(angle) * dist;
    return { bx, by, key };
  });
  return (
    <div style={{ position: 'absolute', top: '50%', left: '50%', width: 0, height: 0 }}>
      {particles.map(p => (
        <div
          key={p.key}
          className="gb-burst"
          style={{
            ['--bx' as any]: `${p.bx}px`,
            ['--by' as any]: `${p.by}px`,
            background: p.key % 2 === 0 ? '#0f380f' : '#306230',
          }}
        />
      ))}
    </div>
  );
}

export function DoorScreen() {
  const { goto, showToast, logout } = useApp();
  const { state, error, setState } = useDoorMachine();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await doorApi.status();
        if (cancelled) return;
        if (!status.configured) {
          setState('not_configured', 'La puerta no está configurada. Pedile al presidente que la configure.');
        }
      } catch (err) {
        if (handle401(err)) {
          showToast('Sesión expirada. Volvé a entrar.', 'error');
          setTimeout(() => { if (!cancelled) { logout(); goto('auth'); } }, 500);
          return;
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [setState, logout, goto, showToast]);

  const open = async () => {
    if (state === 'opening' || state === 'success') return;
    sfx.tick();
    if ('vibrate' in navigator) navigator.vibrate(40);
    setState('opening');
    const minDelay = new Promise<void>((r) => setTimeout(r, 350));
    try {
      const [res] = await Promise.all([doorApi.open('main'), minDelay]);
      if (res.success) {
        sfx.doorOpen();
        if ('vibrate' in navigator) navigator.vibrate([80, 50, 80]);
        setState('success');
        try { localStorage.setItem('vicin-last-door-open', new Date().toISOString()); } catch {}
        showToast('Puerta abierta', 'success');
        // Only auto-nav back if the user is still on the door screen
        setTimeout(() => {
          if (useAppStore.getState().screen === 'door') goto('home');
        }, 2500);
      } else {
        sfx.error();
        if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
        setState('error', res.error || 'No se pudo abrir');
        setTimeout(() => setState('idle'), 2000);
      }
    } catch (err) {
      sfx.error();
      if ('vibrate' in navigator) navigator.vibrate([200, 100, 200]);
      if (handle401(err)) {
        showToast('Sesión expirada. Volvé a entrar.', 'error');
        setTimeout(() => { logout(); goto('auth'); }, 500);
        return;
      }
      const msg = err instanceof ApiError ? err.message : 'Error de red';
      setState('error', msg);
      setTimeout(() => setState('idle'), 2000);
    }
  };

  // A = open, B = back
  const handlersRef = useRef({ state, open, goto });
  handlersRef.current = { state, open, goto };

  useEffect(() => {
    const onA = () => {
      const s = handlersRef.current.state;
      if (s === 'idle' || !s) handlersRef.current.open();
    };
    const onB = () => handlersRef.current.goto('home');
    window.addEventListener('vicin-gb:a', onA);
    window.addEventListener('vicin-gb:b', onB);
    return () => {
      window.removeEventListener('vicin-gb:a', onA);
      window.removeEventListener('vicin-gb:b', onB);
    };
  }, []);

  return (
    <div className="gb-screen">
      <div className="gb-status">
        <div className="gb-status-left">
          <span className="gb-status-dot" />
          <span className="gb-pix gb-pix-xs">PUERTA</span>
        </div>
        <div className="gb-status-right">
          <span className="gb-pix gb-pix-xs">PTA · PRINCIPAL</span>
        </div>
      </div>

      <div className="gb-body">
        {checking ? (
          <div className="gb-center">
            <LoadingSprite size={32} />
            <div className="gb-pix gb-pix-sm">VERIFICANDO...</div>
          </div>
        ) : state === 'not_configured' ? (
          <NotConfigured onBack={() => goto('home')} />
        ) : (
          <div className="gb-center" style={{ gap: 10 }}>
            <div
              className="gb-door-sprite"
              data-state={state}
              style={{ width: 96, height: 96, position: 'relative' }}
            >
              {state === 'success' ? <CheckSprite size={64} /> :
                state === 'opening' ? <DoorOpenSprite size={64} /> :
                  state === 'error' ? <XSprite size={48} /> :
                    <DoorSprite size={64} />}
              {state === 'success' && <ParticleBurst />}
            </div>

            <div className="gb-pix gb-pix-sm" style={{ textAlign: 'center' }}>
              {state === 'success' && '¡ABIERTA!'}
              {state === 'opening' && 'ABRIENDO...'}
              {state === 'error' && 'ERROR'}
              {(state === 'idle' || (!state)) && 'PRESIONA A'}
            </div>

            {/* Game-style progress bar durante opening */}
            {state === 'opening' && (
              <div className="gb-bar" style={{ width: 80, height: 6 }}>
                <div className="gb-bar-fill" data-stripe="true" style={{ animation: 'gb-progress 700ms linear forwards' }} />
              </div>
            )}

            {error && state === 'error' && (
              <div className="gb-dialog gb-shake" style={{ maxWidth: 200, fontSize: 7 }}>
                {error}
              </div>
            )}

            {(state === 'idle' || !state) && (
              <>
                <button
                  onClick={open}
                  className="gb-action"
                  data-testid="door-open"
                  style={{ minWidth: 140 }}
                >
                  ABRIR
                </button>
                <LastOpen />
              </>
            )}

            <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, display: 'none' }}>
              [A] ABRIR · [B] VOLVER
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LastOpen() {
  const [iso, setIso] = useState<string | null>(null);
  useEffect(() => {
    try { setIso(localStorage.getItem('vicin-last-door-open')); } catch {}
  }, []);
  if (!iso) {
    return (
      <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, textAlign: 'center' }}>
        PRIMERA APERTURA
      </div>
    );
  }
  return (
    <div className="gb-pix gb-pix-xs gb-mid-text" style={{ fontSize: 5, textAlign: 'center' }}>
      ÚLTIMA: {formatRelative(iso)}
    </div>
  );
}

function NotConfigured({ onBack }: { onBack: () => void }) {
  return (
    <div className="gb-center" style={{ gap: 10 }}>
      <XSprite size={48} />
      <div className="gb-pix gb-pix-md" style={{ textAlign: 'center' }}>NO CONFIGURADA</div>
      <div className="gb-dialog" style={{ maxWidth: 220, fontSize: 7, textAlign: 'center' }}>
        Pedile al presidente que configure la puerta desde el panel de administración.
      </div>
      <button onClick={onBack} className="gb-action">VOLVER</button>
    </div>
  );
}

/** Hook so App can wire up the A/B buttons. */
export function useDoorHandlers(
  state: string,
  open: () => void,
  goto: (s: 'home') => void,
) {
  return {
    onA: () => { if (state === 'idle' || !state) open(); },
    onB: () => goto('home'),
  };
}
